import React, { useCallback } from 'react';
import Markdown from 'markdown-to-jsx';
import classNames from 'classnames';
import { useNavigate } from 'react-router-dom';
import {
  setTypingAnimation,
  setTypingChunkCount,
  selectPlanRecommenderContext,
} from 'modules/chatbot/slices/chatbotSlice';
import { useAppDispatch, useAppSelector } from 'hooks/redux';
import useBenguide from 'hooks/benguide';
import { ReactComponent as SparkleIcon } from 'images/chatbot/ai-assistant-sparkle-icon.svg';
import { EventType, MessageStatus } from 'modules/chatbot/types/chatbot.types';
import { Sender } from 'modules/chatbot/enums/Sender';
import CollapsibleSources, {
  SourceItem,
} from 'modules/chatbot/components/CollapsibleSources/CollapsibleSources';
import Typewriter from 'modules/aiAssistantChat/components/Typewriter/Typewriter';
import { CHATBOT_TEST_IDS } from 'modules/chatbot/constants/testIds';
import { baseApi } from 'utils/apiUtil';
import { http } from 'utils/httpUtil';
import { trackEvents } from 'utils/initGA4';
import styles from './botMessageBubble.module.less';

type BotMessageBubbleProps = {
  content?: string;
  isPending?: boolean;
  messageId?: string;
  eventType?: EventType;
  status?: MessageStatus;
  sources?: SourceItem[];
  animate?: boolean;
  sender?: Sender;
};

const BotMessageBubble: React.FC<BotMessageBubbleProps> = ({
  content,
  isPending = false,
  messageId,
  eventType,
  status,
  sources = [],
  animate = false,
  sender = Sender.BOT,
}: BotMessageBubbleProps) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const benguide = useBenguide();
  const { urlHash: hash, employerName, employerId, masterId } = benguide;
  const planRecommenderContext = useAppSelector(selectPlanRecommenderContext);
  const answers = planRecommenderContext?.answers;
  const isSystemMessage = sender === Sender.SYSTEM;
  const isError =
    status === MessageStatus.FAILED || eventType === EventType.ERROR;

  const isStatusMessage = eventType === EventType.STATUS;
  const isCompleted = eventType === EventType.COMPLETE;
  const isEmptyMessage = !content || content.trim() === '';

  const onTypingEnd = () => {
    dispatch(
      setTypingAnimation({
        isTyping: false,
        chunkCount: undefined,
        messageId: null,
      })
    );
  };

  const handleScrollIntoView = useCallback(
    (events?: number) => {
      dispatch(setTypingChunkCount(events!));
    },
    [dispatch]
  );

  // Handle PDF download for plan recommender reports
  const downloadResultPdf = useCallback(() => {
    if (!masterId || !answers) {
      console.warn('Missing masterId or answers for PDF download');
      return;
    }

    const ApiUrl = `${baseApi}/v2/benefit-guides/${masterId}/plan-recommender/MEDICAL/pdf`;
    http
      .post(ApiUrl, answers, {
        responseType: 'blob',
        headers: {
          'Content-Type': 'application/json',
        },
      })
      .then((response) => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        const fileName = decodeURIComponent(
          response.request
            .getResponseHeader('Content-Disposition')
            .split('filename=')[1]
        );
        if (typeof (window.navigator as any).msSaveBlob === 'function') {
          (window.navigator as any).msSaveBlob(response.data, fileName);
        } else {
          link.setAttribute('download', fileName);
          document.body.appendChild(link);
          link.click();

          trackEvents({
            category: 'Plan Recommender',
            action: 'download_plan_recommender_pdf',
            label: 'Download Plan Recommender PDF',
          });
        }
      })
      .catch((error) => {
        console.error('Failed to download PDF:', error);
      });
  }, [answers, masterId]);

  // Handle plan click - navigate to specific health benefit URL
  const handlePlanClick = useCallback(
    (benefitKindUrl: any) => {
      if (benefitKindUrl && typeof benefitKindUrl === 'string') {
        // Only use 'health-benefit' path for medical, direct routes for others
        const healthBenefitUrl = `/${hash}${
          employerName ? '/' + employerName : ''
        }/${benefitKindUrl}`;

        // Navigate to the specific health benefit page (client-side navigation)
        navigate(healthBenefitUrl);
      } else {
        console.warn('Invalid benefit kind received:', benefitKindUrl);
      }
    },
    [hash, employerName, navigate]
  );

  // Custom link handler for markdown links
  const CustomLink = useCallback(
    ({ href, children, ...props }: any) => {
      const handleClick = (e: React.MouseEvent) => {
        // Check if this is a plan recommender PDF download link
        if (href && href.includes('PLAN_RECOMMENDER')) {
          e.preventDefault();
          downloadResultPdf();
          return;
        }

        // For other links, use default behavior (open in new tab)
      };

      return (
        <a
          href={href}
          onClick={handleClick}
          target="_blank"
          rel="noopener noreferrer"
          {...props}
        >
          {children}
        </a>
      );
    },
    [downloadResultPdf]
  );

  const renderBotContent = () => {
    if (isPending || isEmptyMessage) {
      return (
        <span data-testid={CHATBOT_TEST_IDS.BOT_MESSAGE_PENDING}>
          <></>
        </span>
      );
    } else {
      return (
        <div
          className={styles.contentWrapper}
          data-testid={CHATBOT_TEST_IDS.BOT_MESSAGE_CONTENT + '_' + messageId}
        >
          {animate ? (
            <Typewriter
              text={content!}
              onTypingEnd={onTypingEnd}
              scrollIntoView={handleScrollIntoView}
              data-testid={CHATBOT_TEST_IDS.BOT_MESSAGE_TYPEWRITER}
              speed={25}
            />
          ) : (
            <Markdown
              data-testid={CHATBOT_TEST_IDS.BOT_MESSAGE_MARKDOWN}
              options={{
                overrides: {
                  a: CustomLink,
                },
              }}
            >
              {content!}
            </Markdown>
          )}
          {isCompleted && (
            <div
              className={styles.messageFooter}
              data-testid={CHATBOT_TEST_IDS.BOT_MESSAGE_FOOTER}
            >
              <CollapsibleSources
                sources={sources}
                messageId={messageId}
                isPending={isPending}
                onPlanClick={handlePlanClick}
                data-testid={CHATBOT_TEST_IDS.BOT_MESSAGE_SOURCES}
                employerId={employerId}
              />
            </div>
          )}
        </div>
      );
    }
  };

  if (isStatusMessage) return <></>;

  if (isPending || isEmptyMessage)
    return (
      <span data-testid={CHATBOT_TEST_IDS.BOT_MESSAGE_PENDING}>
        <></>
      </span>
    );

  return (
    <div
      className={classNames(styles.chatBubble, styles.botBubble)}
      data-testid={CHATBOT_TEST_IDS.BOT_MESSAGE}
      data-message-id={messageId}
    >
      <div
        className={styles.layoutWrapper}
        data-testid={CHATBOT_TEST_IDS.BOT_MESSAGE_LAYOUT}
      >
        <div
          className={styles.icon}
          data-testid={CHATBOT_TEST_IDS.BOT_MESSAGE_ICON}
        >
          <SparkleIcon />
        </div>
        <div
          className={classNames(
            styles.content,
            styles.botBubbleWrapper,
            isError ? styles.errorColor : '',
            isSystemMessage ? styles.systemColor : ''
          )}
          data-testid={CHATBOT_TEST_IDS.BOT_MESSAGE_BUBBLE}
        >
          {renderBotContent()}
        </div>
      </div>
    </div>
  );
};

export default BotMessageBubble;
